import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

const formatValue = (value, formatter) => {
  if (value == null) return "";

  if (typeof formatter === "function") {
    return formatter(value);
  }

  switch (formatter) {
    case "integer":
      return Number(value).toLocaleString();
    case "decimal":
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    case "currency":
      return Number(value).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      });
    case "percent":
      return `${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })}%`;
    default:
      return typeof value === "number" ? value.toLocaleString() : String(value);
  }
};

const isLegacyFormat = (historyData) =>
  Array.isArray(historyData);

const isAdvancedFormat = (historyData) =>
  !Array.isArray(historyData) &&
  historyData &&
  Array.isArray(historyData.categories) &&
  Array.isArray(historyData.series);

const SummaryHistoryChart = ({
  historyData = [],
  chartColor = "#6c757d",
  isMobile,
}) => {
  const chartOptions = useMemo(() => {
    if (!historyData) return null;

    if (isLegacyFormat(historyData)) {
      if (!historyData.length) return null;

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
        },
        grid: {
          left: 40,
          right: 10,
          top: 40,
          bottom: 70,
        },
        xAxis: {
          type: "category",
          data: historyData.map((item) => item.label || item.month),
          axisLabel: {
            rotate: isMobile ? 65 : 45,
          },
          axisTick: {
            alignWithLabel: true,
          },
        },
        yAxis: {
          type: "value",
        },
        series: [
          {
            name: "Value",
            data: historyData.map((item) => item.value ?? 0),
            type: "bar",
            barMaxWidth: 36,
            itemStyle: {
              color: chartColor,
            },
            label: {
              show: true,
              position: "top",
              formatter: ({ value }) =>
                typeof value === "number" ? value.toLocaleString() : value,
              fontSize: isMobile ? 9 : 11,
            },
          },
        ],
      };
    }

    if (isAdvancedFormat(historyData)) {
      const yAxes =
        Array.isArray(historyData.yAxes) && historyData.yAxes.length > 0
          ? historyData.yAxes
          : [{ name: "", position: "left", formatter: "integer" }];

      return {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
          },
          formatter: (params) => {
            if (!params?.length) return "";

            const title = params[0]?.axisValueLabel || "";
            const rows = params
              .map((item) => {
                const axisIndex = item.seriesIndex != null
                  ? historyData.series[item.seriesIndex]?.yAxisIndex ?? 0
                  : 0;
                const axisConfig = yAxes[axisIndex] || {};
                const value = Array.isArray(item.value) ? item.value[1] : item.value;

                return `
                  <div style="display:flex;align-items:center;gap:8px;">
                    <span style="
                      display:inline-block;
                      width:10px;
                      height:10px;
                      border-radius:50%;
                      background:${item.color};
                    "></span>
                    <span>${item.seriesName}: ${formatValue(
                      value,
                      axisConfig.formatter
                    )}</span>
                  </div>
                `;
              })
              .join("");

            return `
              <div>
                <div style="margin-bottom:6px;"><strong>${title}</strong></div>
                ${rows}
              </div>
            `;
          },
        },
        legend: {
          top: 0,
          left: "center",
          type: "scroll",
        },
        grid: {
          left: 50,
          right: yAxes.length > 1 ? 55 : 15,
          top: 60,
          bottom: 70,
        },
        xAxis: {
          type: "category",
          data: historyData.categories,
          axisLabel: {
            rotate: isMobile ? 65 : 45,
          },
          axisTick: {
            alignWithLabel: true,
          },
        },
        yAxis: yAxes.map((axis, index) => ({
          type: "value",
          name: axis.name || "",
          position: axis.position || (index === 0 ? "left" : "right"),
          alignTicks: true,
          axisLabel: {
            formatter: (value) => formatValue(value, axis.formatter),
          },
          splitLine: {
            show: index === 0,
          },
        })),
        series: historyData.series.map((series) => ({
          name: series.name,
          type: series.type || "bar",
          yAxisIndex: series.yAxisIndex ?? 0,
          data: Array.isArray(series.data) ? series.data : [],
          barMaxWidth: 28,
          itemStyle: {
            color: series.color || chartColor,
          },
          label: {
            show: false,
          },
          emphasis: {
            focus: "series",
          },
        })),
      };
    }

    return null;
  }, [historyData, chartColor, isMobile]);

  if (!chartOptions) return null;

  return (
    <div className="card mb-3 chart">
      <div className="card-body">
        <h6 className="mb-3">Statistics</h6>
        <ReactECharts
          option={chartOptions}
          style={{ height: "360px", width: "100%" }}
          notMerge={true}
          lazyUpdate={true}
        />
      </div>
    </div>
  );
};

export default SummaryHistoryChart;
